import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, MapPin, Clock, Utensils, Camera, Plane, Building2, Bus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CAT = {
  food: { icon: Utensils, cls: "bg-amber-50 text-amber-800 border-amber-200" },
  activity: { icon: Camera, cls: "bg-rose-50 text-rose-800 border-rose-200" },
  flight: { icon: Plane, cls: "bg-sky-50 text-sky-800 border-sky-200" },
  hotel: { icon: Building2, cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  transport: { icon: Bus, cls: "bg-indigo-50 text-indigo-800 border-indigo-200" },
};

function AddActivityDialog({ tripId, dayNumber, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ time: "", title: "", notes: "", category: "activity", location_name: "", cost: 0 });
  const save = async () => {
    if (!f.title) { toast.error("Title required"); return; }
    try {
      const { data } = await api.post(`/trips/${tripId}/days/${dayNumber}/activities`, f);
      onDone(data); setOpen(false); setF({ time: "", title: "", notes: "", category: "activity", location_name: "", cost: 0 });
      toast.success("Activity added");
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <>
      <Button variant="outline" size="sm" onClick={()=>setOpen(true)} data-testid={`add-activity-day-${dayNumber}`} className="border-dashed">
        <Plus size={14} className="mr-1"/> Add activity
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-[#FAF7F2]">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">New activity · Day {dayNumber}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Time</Label><Input type="time" value={f.time} onChange={(e)=>setF({...f, time: e.target.value})} className="mt-1" data-testid="activity-time-input"/></div>
              <div><Label>Category</Label>
                <select value={f.category} onChange={(e)=>setF({...f, category: e.target.value})} data-testid="activity-category-select" className="mt-1 h-10 w-full rounded-md border border-stone-300 bg-white px-3 text-sm">
                  {Object.keys(CAT).map(k=><option key={k} value={k}>{k}</option>)}
                </select>
              </div>
            </div>
            <div><Label>Title</Label><Input value={f.title} onChange={(e)=>setF({...f, title: e.target.value})} className="mt-1" data-testid="activity-title-input"/></div>
            <div><Label>Location</Label><Input value={f.location_name} onChange={(e)=>setF({...f, location_name: e.target.value})} className="mt-1" data-testid="activity-location-input"/></div>
            <div><Label>Notes</Label><Textarea value={f.notes} onChange={(e)=>setF({...f, notes: e.target.value})} className="mt-1 min-h-20" data-testid="activity-notes-input"/></div>
            <div><Label>Cost (USD)</Label><Input type="number" value={f.cost} onChange={(e)=>setF({...f, cost: Number(e.target.value)})} className="mt-1" data-testid="activity-cost-input"/></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#C85A32] hover:bg-[#B04B26]" data-testid="activity-save-button">Save activity</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function DayTimeline({ trip, onUpdate }) {
  const del = async (dayNum, aid) => {
    try {
      const { data } = await api.delete(`/trips/${trip.trip_id}/days/${dayNum}/activities/${aid}`);
      onUpdate(data); toast.success("Removed");
    } catch { toast.error("Failed"); }
  };
  const addDay = async () => {
    try { const { data } = await api.post(`/trips/${trip.trip_id}/days`); onUpdate(data); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-8">
      {trip.days.map(d => (
        <section key={d.day_number} className="rounded-2xl bg-white border border-stone-200 p-6" data-testid={`day-section-${d.day_number}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono-code text-[10px] uppercase tracking-[0.25em] text-amber-700">Day {String(d.day_number).padStart(2, "0")}</div>
              <h3 className="font-serif-display text-2xl font-semibold">{d.theme || `Day ${d.day_number}`}</h3>
            </div>
            <AddActivityDialog tripId={trip.trip_id} dayNumber={d.day_number} onDone={onUpdate}/>
          </div>
          {d.activities.length === 0 ? (
            <div className="text-stone-500 text-sm italic border border-dashed border-stone-300 rounded-lg p-6 text-center">
              No activities yet. Add your first stop for this day.
            </div>
          ) : (
            <ol className="space-y-3">
              {d.activities.map((a, i) => {
                const C = CAT[a.category] || CAT.activity;
                const Icon = C.icon;
                return (
                  <li key={a.id} className="relative pl-10 group" data-testid={`activity-${a.id}`}>
                    <div className="absolute left-0 top-1 w-8 h-8 rounded-full bg-stone-900 text-white flex items-center justify-center font-mono-code text-xs">{i+1}</div>
                    <div className="rounded-xl border border-stone-200 bg-stone-50/60 p-4 flex items-start gap-3">
                      <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${C.cls} border`}><Icon size={16}/></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {a.time && <span className="font-mono-code text-xs text-stone-500 flex items-center gap-1"><Clock size={10}/>{a.time}</span>}
                          <span className={`font-mono-code text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${C.cls}`}>{a.category}</span>
                          {a.cost > 0 && <span className="font-mono-code text-xs text-stone-600">${a.cost}</span>}
                        </div>
                        <div className="font-semibold text-stone-900 mt-1">{a.title}</div>
                        {a.location_name && <div className="text-xs text-stone-500 flex items-center gap-1 mt-0.5"><MapPin size={11}/> {a.location_name}</div>}
                        {a.notes && <div className="text-sm text-stone-600 mt-2">{a.notes}</div>}
                      </div>
                      <button onClick={()=>del(d.day_number, a.id)} data-testid={`activity-delete-${a.id}`} className="opacity-0 group-hover:opacity-100 transition text-stone-400 hover:text-red-600">
                        <Trash2 size={16}/>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      ))}
      <Button variant="outline" onClick={addDay} className="w-full border-dashed" data-testid="add-day-button"><Plus size={14} className="mr-1"/> Add another day</Button>
    </div>
  );
}
